import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../api/axiosInstance"; // 🔒 JWT-authenticated API
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import LoadingSpinner from "./LoadingSpinner";
import OnboardingSlider from "./OnboardingSlider";
import { ONBOARDING_TO_PRODUCTS_KEY } from "./StoreReady";
import ActivePlanSummary from "./ActivePlanSummary";
import { onboardingAction } from "../utils/onboarding";
import { countOwnProducts } from "../utils/productCount";
import { escapeHtml } from "../utils/escapeHtml";
import {
  shippingStatus,
  setupSteps,
  needsWeight,
  weightAndSize,
  productsMissingWeight,
} from "../utils/shippingStatus";
import { StoreSetupChecklist, ConnectCourierModal, MissingWeightNotice } from "./ShippingSetup";
import "datatables.net-dt/css/jquery.dataTables.css";
import $ from "jquery";
import "datatables.net";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { usePlan } from "../context/PlanContext";
import subscriptionService from "../services/subscriptionService";
import paymentManagementService, { PAYMENT_TYPE } from "../services/paymentManagementService";


const COURIER_STEP_SKIPPED_KEY = "courierStepSkipped";

function GiftList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canAddProduct, planInfo } = usePlan();
  
  const [giftData, setGiftData] = useState([]);
  const [deleteGiftId, setDeleteGiftId] = useState("");
  const [deleteTitle, setDeleteTitle] = useState("");
  const [dataTable, setDataTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLength, setPageLength] = useState(5);

  const storeUrl = localStorage.getItem("store_url");
  const isMobile = window.innerWidth <= 768;

  const [businessId, setBusinessId] = useState('');

  // Onboarding slider states
  // Initialize to false to prevent flash, will be set by useEffect after data loads
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Subscription states
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Shipping setup (eventseasyadmin#299): the store info as the API sent it,
  // the "Connect your courier" step (skipped for this tab) and the table
  // narrowed to products with no weight
  const [storeInfo, setStoreInfo] = useState(null);
  const [courierSkipped, setCourierSkipped] = useState(() => {
    try {
      return sessionStorage.getItem(COURIER_STEP_SKIPPED_KEY) === "true";
    } catch (e) {
      return false;
    }
  });
  const [onlyMissingWeight, setOnlyMissingWeight] = useState(false);

  useEffect(() => {
    fetchData();
    const savedPageLength = localStorage.getItem('giftListPageLength');
    if (savedPageLength) {
      setPageLength(parseInt(savedPageLength));
    }

    // Fetch subscription and payment info
    const business_id = localStorage.getItem("business_id");
    if (business_id) {
      fetchSubscriptionInfo(business_id);
      fetchPaymentInfo(business_id);
    }
  }, []);

  // COMMENTED OUT - Plan modal no longer shown initially
  // const checkIfNewUser = async () => {
  //   try {
  //     const business_id = localStorage.getItem("business_id");
  //     const hasSeenPlanModal = localStorage.getItem("hasSeenPlanModal");
  //     
  //     if (!business_id || hasSeenPlanModal === "true") {
  //       return;
  //     }

  //     // Check if user has a plan
  //     const response = await api.get('/business/plan/info', { params: { business_user_id: business_id } });
  //     
  //     if (response.data.status === "success" && response.data.result) {
  //       const result = response.data.result;
  //       // If user has no plan or plan status is "none", show modal
  //       if (!result.plan_id || result.plan_status === "none" || result.is_expired) {
  //         setIsNewUser(true);
  //         setShowPlanModal(true);
  //       } else {
  //         // User has a plan, mark as seen
  //         localStorage.setItem("hasSeenPlanModal", "true");
  //       }
  //     } else {
  //       // No plan found, show modal
  //       setIsNewUser(true);
  //       setShowPlanModal(true);
  //     }
  //   } catch (error) {
  //     console.error("Error checking user plan:", error);
  //     // On error, show modal to be safe
  //     setIsNewUser(true);
  //     setShowPlanModal(true);
  //   }
  // };

  // Check and show onboarding after giftData is loaded
  useEffect(() => {
    const action = onboardingAction({
      // If the user has already seen onboarding, never show it again
      hasSeen: localStorage.getItem('hasSeenOnboarding') === 'true',
      businessId: localStorage.getItem("business_id"),
      loading,
      productCount: giftData.length,
      fromStoreReady: Boolean(sessionStorage.getItem(ONBOARDING_TO_PRODUCTS_KEY)),
    });
    console.log('[ONBOARDING CHECK]', { action, giftDataLength: giftData.length, loading });

    if (action === "hide") {
      setShowOnboarding(false);
    } else if (action === "mark-seen") {
      // User has products, so mark onboarding as seen and hide it
      localStorage.setItem('hasSeenOnboarding', 'true');
      setShowOnboarding(false);
    } else if (action === "show") {
      setShowOnboarding(true);
    }
  }, [giftData, loading]); // Run when giftData or loading changes

  // Fetch subscription info
  const fetchSubscriptionInfo = async (businessId) => {
    try {
      const subInfo = await subscriptionService.getSubscriptionInfo(businessId);
      setSubscriptionInfo(subInfo);
      console.log('[GIFT] Subscription info loaded:', subInfo);
    } catch (error) {
      console.error('[GIFT] Error fetching subscription:', error);
    }
  };

  // Fetch payment management info
  const fetchPaymentInfo = async (businessId) => {
    try {
      const info = await paymentManagementService.getPaymentInfo(businessId);
      setPaymentInfo(info);
      console.log('[GIFT] Payment info loaded:', info);
    } catch (error) {
      console.error('[GIFT] Error fetching payment info:', error);
    }
  };

  // Check product limit (supports both manual pay and subscription)
  const checkProductLimit = () => {
    // The untouched sample product doesn't take a plan slot (same as the API)
    const productCount = countOwnProducts(giftData);

    // Priority 1: Check if user has active subscription
    if (paymentInfo && paymentInfo.paymentType === PAYMENT_TYPE.AUTO_RENEW && subscriptionInfo) {
      const check = subscriptionService.canAddProduct(subscriptionInfo, productCount);
      return check;
    }

    // Priority 2: Fall back to manual plan check
    if (planInfo) {
      const check = canAddProduct(productCount);
      return check;
    }

    // No payment method
    return {
      allowed: false,
      reason: 'No active plan or subscription. Please subscribe or purchase a plan.'
    };
  };



  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
  };

  useEffect(() => {
    if (giftData.length) {
      if (dataTable) {
        dataTable.destroy();
      }
      const missingWeight = productsMissingWeight(giftData);
      const table = $("#gift").DataTable({
        data: onlyMissingWeight && missingWeight.length ? missingWeight : giftData,
        columns: [
          { data: null, render: (data, type, row, meta) => meta.row + 1 },
          {
            data: "url",
            render: (data) =>
              `<div class="prodlist_img"><img src="${data}" alt="Product" border="0" /></div>`,
          },
          { data: "name" },
          {
            data: "quantity",
            render: (data, type, row) => {
              // Check if it's a digital product (quantity is 999999 or is_digital flag is true)
              if (row.is_digital || data === 999999) {
                return '<span style="color: #2E7D4F; font-weight: 600;">Unlimited (Digital Product)</span>';
              }
              return data || 0;
            },
          },
          {
            data: "price",
            render: (data) => `₹${data}`,
          },
          {
            // What the courier charges by: "1.2 kg · 30×20×12 cm", or a link to add it
            data: null,
            render: (data, type, row) => {
              if (!needsWeight(row)) return `<span class="mtk-ship-chip mtk-ship-chip--none">${escapeHtml(t("shipping.weight.notNeeded"))}</span>`;
              const label = weightAndSize(row);
              return label
                ? `<span class="mtk-ship-chip mtk-ship-chip--ok">${escapeHtml(label)}</span>`
                : `<a href="/gift_edit?id=${encodeURIComponent(row._id)}" class="mtk-ship-chip mtk-ship-chip--add">${escapeHtml(t("shipping.weight.add"))}</a>`;
            },
          },
          {
            data: "created_date",
            render: (data) => new Date(data).toLocaleString(),
          },
          {
            data: null,
            render: (data) => {
              return `
      <div align="center">
        <a href="/gift_edit?id=${data._id}" class="crsr_pntr edit-btn" data-id="${data._id}"><i class="fa-solid fa-pen mr-2"></i></a>
        <a href="#" class="crsr_pntr delete-btn" data-id="${data._id}" data-name="${data.name}" data-toggle="modal" data-target="#modaldelete"><i class="fa-solid fa-trash-can"></i></a>
      </div>
    `;
            },
          },
        ],
        pageLength: pageLength, // Use state value instead of hardcoded 5
        lengthMenu: [5, 10, 25, 50, 75, 100],
      });
      setDataTable(table);

      // Store page length when it changes
      table.on('length.dt', function (e, settings, len) {
        setPageLength(len);
        localStorage.setItem('giftListPageLength', len.toString());
      });

      // Store current page length when edit is clicked
      $("#gift").on("click", ".edit-btn", function () {
        const currentLength = table.page.len();
        localStorage.setItem('giftListPageLength', currentLength.toString());
      });

      $("#gift").on("click", ".delete-btn", function () {
        const productId = $(this).data("id");
        const productName = $(this).data("name");
        openDeleteModal(productId, productName);
      });
    }
  }, [giftData, pageLength, onlyMissingWeight]);


  const fetchData = () => {
    setLoading(true);
    const businessId = localStorage.getItem("business_id");
    const signupStatus = localStorage.getItem("signup");
    let params = {};

    // Check the condition based on business_id and signup
    if (businessId && signupStatus === "user") {
      params.business_id = businessId;
    }

    // 🔒 Using JWT-authenticated API (simple & direct)
    api
      .get('/gift/list', { params })
      .then((res) => {
        if (res.data.status === "success") {
          setGiftData(res.data.result);
          console.log('✅ JWT: Gift list loaded successfully');
        } else if (res.data.status === "no_record") {
          setGiftData([]);
          console.log('✅ JWT: No products found');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ JWT Error fetching gift data:", err);
        console.log('⚠️ Falling back to old authentication...');
        // Fallback to old method if JWT fails
        axios
          .get(process.env.REACT_APP_APIURL + "gift/list", { params })
          .then((res) => {
            if (res.data.status === "success") {
              setGiftData(res.data.result);
            } else if (res.data.status === "no_record") {
              setGiftData([]);
            }
            setLoading(false);
          })
          .catch((error) => {
            console.error("Fallback also failed:", error);
            setLoading(false);
          });
      });
  };

 const deleteGift = (gift_id) => {
  const businessId = localStorage.getItem("business_id");
  const signupStatus = localStorage.getItem("signup");
  
  let payload = {
    gift_id: gift_id,
    gift_status: "delete",
  };

  if (businessId && signupStatus === "user") {
    payload.business_id = businessId;
  }
  
  // 🔒 Using JWT-authenticated API (simple & direct)
  api
    .post('/gift/delete', payload)
    .then((res) => {
      if (res.data.status === "success") {
        const updatedList = giftData.filter((product) => product._id !== gift_id);

        // Destroy DataTable before updating state
        if (dataTable) {
          dataTable.destroy();
          setDataTable(null);
        }

        if (updatedList.length === 0) {
          setGiftData([]);
          toast.success(t("product_deleted_successfully"));
          console.log('✅ JWT: Gift deleted successfully');
          setTimeout(() => {
            navigate("/gift");
          }, 100);
        } else {
          setGiftData(updatedList);
          toast.success(t("product_deleted_successfully"));
          console.log('✅ JWT: Gift deleted successfully');
        }
      }
    })
    .catch((err) => {
      console.error("❌ JWT Error deleting product:", err);
      console.log('⚠️ Falling back to old authentication...');
      // Fallback to old method if JWT fails
      axios
        .post(`${process.env.REACT_APP_APIURL}gift/delete`, payload)
        .then((res) => {
          if (res.data.status === "success") {
            const updatedList = giftData.filter((product) => product._id !== gift_id);

            if (dataTable) {
              dataTable.destroy();
              setDataTable(null);
            }

            if (updatedList.length === 0) {
              setGiftData([]);
              toast.success(t("product_deleted_successfully"));
              setTimeout(() => {
                navigate("/gift");
              }, 100);
            } else {
              setGiftData(updatedList);
              toast.success(t("product_deleted_successfully"));
            }
          }
        })
        .catch((error) => {
          console.error("Fallback also failed:", error);
          toast.error("An error occurred while deleting product.");
        });
    });
};
   

     const [storeData, setStoreData] = useState({
       business_name: '',
       store_subdomain: '',
       store_status: 'inactive',
       store_settings: {
         logo_url: '',
         primary_color: '#007bff',
         secondary_color: '#6c757d',
         header_color: '#fff',  // Add this
         footer_color: '#fff',
         accent_color: '#030303ff',
         font_family: 'Arial, sans-serif',
         store_description: '',
         contact_email: '',
         contact_phone: '',
         contact_whatsapp: '',
         social_links: {
           facebook: '',
           instagram: '',
           twitter: ''
         },
         seo_settings: {
           meta_title: '',
           meta_description: '',
           meta_keywords: ''
         },
         banner_section: {
           enabled: false,
           size: 'medium',
           slides: []
         },
         category_section: {
           enabled: false,
           title: 'Shop by Categories',
           selected_categories: []
         },
         featured_section: {
           enabled: false,
           title: 'Featured Products',
           selected_products: []
         }
       }
     });
   
     useEffect(() => {
       const business_id = localStorage.getItem('business_id');
       if (business_id) {
         setBusinessId(business_id);
         fetchStoreInfo(business_id);
   
       }
     }, []);
   
    const updateFetchStoreInfo = (data) => {
  const result = data.result || {};
  setStoreInfo(result);

  const apiSettings = result.store_settings || {};

  setStoreData(prev => ({
    ...prev,
    business_name: result.business_name || "",
    store_subdomain: result.store_subdomain || "",
    store_status: result.store_status || "inactive",

    store_settings: {
      ...prev.store_settings,
      ...apiSettings,

      social_links: {
        ...prev.store_settings.social_links,
        ...(apiSettings.social_links || {})
      },

      seo_settings: {
        ...prev.store_settings.seo_settings,
        ...(apiSettings.seo_settings || {})
      },

      banner_section: {
        ...prev.store_settings.banner_section,
        ...(apiSettings.banner_section || {})
      },

      category_section: {
        ...prev.store_settings.category_section,
        ...(apiSettings.category_section || {})
      },

      featured_section: {
        ...prev.store_settings.featured_section,
        ...(apiSettings.featured_section || {})
      }
    }
  }));

  // Save Store URL
  if (result.store_subdomain) {
    const finalUrl = `https://${result.store_subdomain}.mytamilkart.com`;
    localStorage.setItem("store_url", finalUrl);
  }
};

   
   
     const fetchStoreInfo = async (businessId) => {
       try {
         setLoading(true);
         const response = await api.get('/business/store/info', { params: { business_id: businessId } });
         const data = response.data;
   
         if (data.status === 'success') {
           // Use the updateFetchStoreInfo function to properly set all data
           updateFetchStoreInfo(data);
         } else {
           toast.error('Failed to load store information');
         }
       } catch (error) {
         console.error('Error fetching store info:', error);
         toast.error('Error loading store information');
       } finally {
         setLoading(false);
       }
     };

  
  const openDeleteModal = (id, title) => {
    setDeleteGiftId(id);
    setDeleteTitle(title);
  };

  // Shipping setup: nothing shows while the store info doesn't report it
  const shipping = shippingStatus(storeInfo);
  const shippingKnown = shipping.state !== "unknown";
  const productCount = countOwnProducts(giftData);
  const steps = setupSteps({
    productCount,
    paymentConnected: storeInfo && storeInfo.activation_status === "activated",
    shipping,
    storeLive: storeData.store_status === "active",
  });
  const courierStep = steps.findIndex((s) => s.key === "courier") + 1;
  const missingWeightCount = productsMissingWeight(giftData).length;
  const showCourierStep = shippingKnown && !courierSkipped && !showOnboarding
    && ["not_set_up", "optional", "grace"].includes(shipping.state);
  const skipCourierStep = () => {
    try {
      sessionStorage.setItem(COURIER_STEP_SKIPPED_KEY, "true");
    } catch (e) {
      // Private mode: skipped until the page reloads
    }
    setCourierSkipped(true);
  };

  if (loading) {
    return <LoadingSpinner message="Loading your products..." />;
  }

  return (
    <div className="wrapper" id="root">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-12 ">
                <h1 className="m-0">
                  {t('productTab.title')}
                  <a 
                    onClick={(e) => {
                      e.preventDefault();
                      // Check product limit (supports both manual pay and subscription)
                      const limitCheck = checkProductLimit();
                      
                      if (!limitCheck.allowed) {
                        toast.error(limitCheck.reason, {
                          autoClose: 5000,
                          onClick: () => {
                            // Navigate based on payment type
                            if (paymentInfo && paymentInfo.paymentType === PAYMENT_TYPE.AUTO_RENEW) {
                              navigate('/subscription');
                            } else {
                              navigate('/purchase-plan');
                            }
                          }
                        });
                      } else {
                        navigate('/gift_add');
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i
                      className="fa-solid fa-square-plus ml-2"
                      title="Add Product"
                    />
                  </a>

                </h1>
              </div>
            </div>
          </div>
        </div>



        <div className="content">
          <div className="container-fluid">
            {shippingKnown && (
              <StoreSetupChecklist
                steps={steps}
                productCount={productCount}
                storeLink={storeData.store_subdomain ? `${storeData.store_subdomain}.mytamilkart.com` : ""}
              />
            )}
            {/* The plan and its features; "activated" right after a payment */}
            <ActivePlanSummary productCount={productCount} />
            {/* The courier creates shipments and charges by weight and box size (not Manual / self-ship) */}
            {shippingKnown && shipping.provider !== "manual" && (
              <MissingWeightNotice
                count={missingWeightCount}
                filtered={onlyMissingWeight}
                onToggle={() => setOnlyMissingWeight((on) => !on)}
              />
            )}
            <div className="row">
              <div className="col-lg-12 text-center">
                {giftData.length === 0 ? (
                  <div style={{ marginTop: "150px" }}>
                    <span className="d-block f_black">
                      {t('productTab.noProductsFound.part1')}
                      {/* <br />
                      {t('productTab.noProductsFound.part2')}{' '} */}
                      <Link
                        to="/gift_add"
                        style={{
                          color: '#1E5B3A',
                         
                          cursor: 'pointer',
                          marginLeft: "6px"
                        }}
                      >
                        {t('productTab.noProductsFound.part3')}

                      </Link>{' '}



                    </span>

                    {!isMobile && (
                      <span className="fw-bold">
                        Your store link is
                        <a
      href={`https://${storeData.store_subdomain}.mytamilkart.com`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        marginLeft: "6px",
        color: "#1E5B3A",
        
      }}
    >
      {storeData.store_subdomain
        ? `${storeData.store_subdomain}.mytamilkart.com`
        : `${storeData.store_subdomain}.mytamilkart.com`}
    </a>
                      </span>
                    )}

                    {isMobile && (
                      <span className="fw-bold">
                        <a
                          href={storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1E5B3A",  }}
                        >
                          Open Store
                        </a>
                      </span>
                    )}



                  </div>
                ) : (
                  <div className="table-responsive">
                    <table
                      id="gift"
                      className="table table-striped table-bordered txt_lft"
                      style={{ width: "100%" }}
                    >
                      <thead>
                        <tr>
                          <th>{t('table.sno')}</th>
                          <th>{t('table.product')}</th>
                          <th>{t('table.title')}</th>
                          <th>{t('table.quantity')}</th>
                          <th>{t('table.price')}</th>
                          <th>{t('table.weightSize')}</th>
                          <th>{t('table.addedOn')}</th>
                          <th>{t('table.action')}</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Product
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteTitle}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-primary"
                data-dismiss="modal"
                aria-label="Close"
                onClick={() => deleteGift(deleteGiftId)}
              >
                Yes, delete
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
                aria-label="Close"
              >
                No, cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Slider */}
      {/* Only show onboarding after loading is complete to prevent flash */}
      {showOnboarding && !loading && (
        <OnboardingSlider 
          onClose={handleCloseOnboarding}
          storeUrl={storeData.store_subdomain ? `${storeData.store_subdomain}.mytamilkart.com` : 'storename.mytamilkart.com'}
        />
      )}

      {/* "Connect your courier": after the onboarding story, until set up or skipped */}
      {showCourierStep && (
        <ConnectCourierModal step={courierStep} total={steps.length} onClose={skipCourierStep} />
      )}

      {/* COMMENTED OUT - Plan Selection Modal for New Users */}
      {/* Now showing onboarding slider directly instead */}
      {/* {showPlanModal && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 9999
          }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '900px' }}>
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div 
                className="modal-header" 
                style={{ 
                  background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '20px 15px'
                }}
              >
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: window.innerWidth <= 768 ? '1.3em' : '1.8em', 
                    fontWeight: '700',
                    marginBottom: '5px'
                  }}>
                    🎉 Welcome to MyTamilKart!
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    fontSize: window.innerWidth <= 768 ? '0.85em' : '1em',
                    opacity: 0.95
                  }}>
                    Choose how you'd like to get started
                  </p>
                </div>
              </div>

              <div className="modal-body" style={{ padding: window.innerWidth <= 768 ? '20px 15px' : '30px 20px' }}>
                ... modal content ...
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
}

export default GiftList;
